/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  fetchCampaignStatus,
  fetchRecipientsPreview,
  startShutdownCampaign,
} from '../action/shutdown';
import {
  shutdownHeading,
  shutdownInnerFormStyle,
  shutdownText,
} from '../Shutdown/shutdownSettingsStyles';

const POLL_MS = 5000;
const EMAIL_TYPES = ['announcement', 'reminder', 'final_warning'];

function ShutdownCampaign({ activeTab, setActiveTab }) {
  const { theme } = useSelector((state) => state.theme);
  const [recipients, setRecipients] = useState(null);
  const [campaignId, setCampaignId] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [force, setForce] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const pollRef = useRef(null);

  const loadRecipients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchRecipientsPreview();
      setRecipients(res?.data?.responseData ?? null);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load recipient preview.');
    } finally {
      setLoading(false);
    }
  }, []);

  const pollCampaign = useCallback(async (id) => {
    try {
      const res = await fetchCampaignStatus(id);
      const data = res?.data?.responseData;
      setSchedules(data?.schedules ?? []);
      const allDone = (data?.schedules ?? []).every((s) =>
        ['completed', 'failed', 'cancelled'].includes(s.status),
      );
      if (allDone && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not refresh campaign status.');
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'shutdown-campaign') {
      loadRecipients();
    }
  }, [activeTab, loadRecipients]);

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, []);

  const handleStartCampaign = async (e) => {
    e.preventDefault();
    setStarting(true);
    setError(null);
    setMessage(null);
    try {
      const res = await startShutdownCampaign({ dryRun, force });
      const data = res?.data?.responseData;
      const id = data?.campaignId;
      setCampaignId(id);
      setMessage(data?.message || 'Campaign started.');
      if (id) {
        setSchedules([data?.announcement, data?.reminder, data?.finalWarning].filter(Boolean));
        if (pollRef.current) {
          clearInterval(pollRef.current);
        }
        pollRef.current = setInterval(() => pollCampaign(id), POLL_MS);
        pollCampaign(id);
      }
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message;
      if (status === 409) {
        setError(msg || 'A campaign is already active. Use Force to start a new one.');
      } else {
        setError(msg || 'Failed to start campaign.');
      }
    } finally {
      setStarting(false);
    }
  };

  const tableCellStyle = shutdownText(theme, 0.95);
  const thStyle = { ...shutdownText(theme), textAlign: 'left', padding: '8px', fontWeight: 600 };

  return (
    <li className={`li-setting-tab ${activeTab === 'shutdown-campaign' ? 'active' : ''}`}>
      <div
        className="settings-accordion-tab dark:bg-black"
        onClick={() => setActiveTab((t) => (t === 'shutdown-campaign' ? null : 'shutdown-campaign'))}
        style={{ background: theme === 'dark' ? 'black' : '' }}
        css={{
          background:
            activeTab === 'shutdown-campaign'
              ? theme === 'dark'
                ? 'linear-gradient(0deg,var(--accent-color-10),var(--accent-color-10)),#000 !important'
                : 'linear-gradient(0deg, var(--accent-color), var(--accent-color)), #ffffff !important'
              : '',
          '&:hover': {
            background:
              theme === 'dark'
                ? 'linear-gradient(0deg, var(--accent-color-10), var(--accent-color-10)), #000 !important'
                : 'linear-gradient(0deg, var(--accent-color), var(--accent-color)), #ffffff !important',
          },
        }}
      >
        <div className="settings-accordion-heading">
          <figure className="settings-icon">
            <img src="/images/general.svg" className="img-default dark:invert-color" alt="" />
          </figure>
          <h3 className="accordion-title uppercase dark:text-white">Shutdown emails</h3>
        </div>
        <div className="settings-accordion-arrow">
          <img src="/images/arrow-down.svg" className="dark:invert-color" alt="" />
        </div>
      </div>

      {activeTab === 'shutdown-campaign' && (
        <div className="settings-accordion-content shutdown-accordion-content">
          <div
            className="inner-form shutdown-settings-panel relative"
            style={shutdownInnerFormStyle(theme)}
          >
            <h3 className="form-heading uppercase" style={shutdownHeading(theme)}>
              Email campaign
            </h3>
            <p className="field-label" style={{ ...shutdownText(theme), marginTop: '12px', marginBottom: '16px', fontSize: '14px' }}>
              Sends announcement now and schedules reminder and final warning emails.
            </p>

            {loading && (
              <p
                className="shutdown-recipients-line"
                style={{ ...shutdownText(theme), fontSize: '14px', display: 'block' }}
              >
                Loading recipients…
              </p>
            )}
            {recipients && (
              <div
                className="shutdown-recipients-info"
                style={{ ...shutdownText(theme), marginBottom: '16px', fontSize: '14px', display: 'block' }}
              >
                <p
                  className="shutdown-recipients-line"
                  style={{ ...shutdownText(theme), margin: '0 0 8px', fontSize: '14px', display: 'block' }}
                >
                  <strong>{recipients.total}</strong> recipients
                </p>
                {recipients.sample?.length > 0 && (
                  <p
                    className="shutdown-recipients-line shutdown-recipients-sample"
                    style={{
                      ...shutdownText(theme, 0.75),
                      margin: 0,
                      fontSize: '13px',
                      lineHeight: 1.5,
                      display: 'block',
                      wordBreak: 'break-word',
                    }}
                  >
                    e.g. {recipients.sample.map((s) => s.email).join(', ')}
                  </p>
                )}
              </div>
            )}

            <form onSubmit={handleStartCampaign}>
              <label
                className="flex items-center gap-2 mb-2"
                style={{ ...shutdownText(theme), fontSize: '14px', cursor: 'pointer' }}
              >
                <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
                Dry run (test send only)
              </label>
              <label
                className="flex items-center gap-2 mb-4"
                style={{ ...shutdownText(theme), fontSize: '14px', cursor: 'pointer' }}
              >
                <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
                Force (cancel pending and restart)
              </label>
              <div className="field-block">
                <button
                  type="submit"
                  disabled={starting || loading}
                  className="changeName"
                  css={{
                    background:
                      theme === 'dark'
                        ? `linear-gradient(0deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), linear-gradient(0deg, var(--accent-color), var(--accent-color)), linear-gradient(0deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05)), #000000 !important`
                        : `linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), linear-gradient(0deg, var(--accent-color), var(--accent-color)), linear-gradient(0deg, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05)), #ffffff !important`,
                    color: 'var(--accent-color) !important',
                    opacity: starting || loading ? 0.7 : 1,
                    cursor: starting || loading ? 'wait' : 'pointer',
                  }}
                >
                  {starting ? 'Starting…' : 'Start shutdown email campaign'}
                </button>
              </div>
            </form>

            {message && (
              <p style={{ color: 'var(--accent-color)', marginTop: '16px', fontSize: '14px', opacity: 1 }}>
                {message}
              </p>
            )}
            {error && (
              <p style={{ color: '#f87171', marginTop: '16px', fontSize: '14px', opacity: 1 }}>{error}</p>
            )}

            {campaignId && (
              <div className="shutdown-campaign-status" style={{ marginTop: '24px' }}>
                <p style={{ ...shutdownText(theme), marginBottom: '8px', fontSize: '13px' }}>
                  Campaign:{' '}
                  <code style={shutdownText(theme, 1)}>{campaignId}</code>
                </p>
                <table className="time-tracker-table" style={{ width: '100%', fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Type</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Sent</th>
                      <th style={thStyle}>Failed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(schedules.length
                      ? schedules
                      : EMAIL_TYPES.map((t) => ({ emailType: t, status: '—' }))
                    ).map((row) => (
                      <tr key={row.id ?? row.emailType}>
                        <td style={tableCellStyle}>{row.emailType}</td>
                        <td style={tableCellStyle}>{row.status}</td>
                        <td style={tableCellStyle}>
                          {row.sentCount != null
                            ? `${row.sentCount}/${row.totalCount ?? '?'}`
                            : '—'}
                        </td>
                        <td style={tableCellStyle}>{row.failedCount ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

export default ShutdownCampaign;
